import { useManager } from 'dreamstate';
import { default as cloneDeep } from 'lodash/cloneDeep';
import { MutableRefObject, useEffect, useRef } from 'react';

import { Optional } from '@vimeo/core/types/generic';
import { PollManager } from '@vimeo/live-core/store/firebase/interaction/poll';
import { QnAManager } from '@vimeo/live-core/store/firebase/interaction/qna';
import { IPoll, IQnASession } from '@vimeo/live-core/types/interaction_tools';
import { postPageMessage } from '@vimeo/live-core/utils/embed/embed_messaging';
import { EInteractionEmbedSignal } from '@vimeo/live-core/utils/embed/embed_signals';

/**
 * Observe and emit external signals for embed handlers.
 * For docs check: live/docs/INTERACTION_WIDGET_SIGNALS.md
 */
export function InteractionToolsAmbientEmbedSignaling({
  // eslint-disable-next-line react-hooks/rules-of-hooks
  pollContext: { polls, activePollId } = useManager(PollManager),
  // eslint-disable-next-line react-hooks/rules-of-hooks
  qnaContext: { activeSession: activeQnaSession } = useManager(QnAManager, ({ activeSession }) => [
    activeSession,
  ]),
}) {
  const activePollRef: MutableRefObject<Optional<IPoll>> = useRef(null);
  const activeQnaRef: MutableRefObject<Optional<IQnASession>> = useRef(null);

  /**
   * Observe active poll changes and notify external world about activity.
   */
  useEffect(() => {
    const currentActivePoll: Optional<IPoll> = activePollRef.current;
    const newActivePoll: Optional<IPoll> =
      Object.values(polls).find((it) => it.id === activePollId) ?? null;

    if (currentActivePoll?.id !== newActivePoll?.id) {
      activePollRef.current = newActivePoll;
      postPageMessage(
        EInteractionEmbedSignal.ACTIVE_POLL_CHANGED,
        newActivePoll ? cloneDeep(newActivePoll) : null,
        true,
      );
    }
  }, [polls, activePollId]);

  /**
   * Observe active qna session and notify external world about activity.
   */
  useEffect(() => {
    const currentActiveQna: Optional<IQnASession> = activeQnaRef.current;
    const newActiveQna: Optional<IQnASession> = activeQnaSession;

    if (currentActiveQna?.id !== newActiveQna?.id) {
      activeQnaRef.current = newActiveQna;
      postPageMessage(
        EInteractionEmbedSignal.ACTIVE_QNA_CHANGED,
        newActiveQna ? cloneDeep(newActiveQna) : null,
        true,
      );
    }
  }, [activeQnaSession]);

  return null;
}
